/**
 * WHICH REPORT ANSWERS WHICH QUESTION, AND WHAT IT RAISES NEXT.
 *
 * The product has thirty-five report surfaces and, until this file, no stated
 * order to read them in. That is survivable for someone who built it and fatal
 * for everyone else: on a live demo the founder could not decide what to open
 * next, and a client's performance team has the same problem every morning
 * with no one to ask.
 *
 * Two things this file deliberately is NOT.
 *
 * It is not a renaming. Every `title` here is the name already on the sidebar,
 * because the teams using them know those names and the cost of changing them
 * falls on people who have already learned the product.
 *
 * It is not a single path. Nobody reads thirty-five reports. A founder opens
 * four; the ads team lives in the campaign depth; retention reads a different
 * five. `who` is what makes the list usable - the order is real, but you walk
 * the part of it that is yours.
 *
 * `next` is the connective tissue and the point of the whole file: every
 * report ends by naming the question it raises and linking to the report that
 * answers it, so a reader never has to return to the sidebar mid-thought.
 */

export const ROLES = [
  { key: 'all', label: 'Everyone' },
  { key: 'founder', label: 'Founder' },
  { key: 'performance', label: 'Performance / ads' },
  { key: 'retention', label: 'Retention / CRM' },
  { key: 'ops', label: 'Ops / data' },
];

/** The reading order, as a sequence. The number is the layer, not a rank. */
export const STAGES = [
  { n: 0, key: 'trust', label: 'Can I trust this?' },
  { n: 1, key: 'position', label: 'How am I doing?' },
  { n: 2, key: 'movement', label: 'What changed, and why?' },
  { n: 3, key: 'money', label: 'Where did the money go?' },
  { n: 4, key: 'decide', label: 'So what do I change?' },
  { n: 5, key: 'diagnose', label: 'Why is it happening?' },
  { n: 6, key: 'economics', label: 'Is this worth scaling?' },
  { n: 7, key: 'growth', label: 'What is holding me back?' },
];

/**
 * One entry per report a client is meant to READ. Records and inputs - Orders,
 * Customers, Costs - are not here on purpose: they are things you look up, not
 * reports you are walked through, and listing them would put the guide back
 * where the sidebar already is.
 */
export const REPORTS = [
  {
    key: 'signal-health', to: '/signal-health', title: 'Signal Health', stage: 'trust',
    asks: 'Is tracking collecting, and tied to campaigns?',
    gives: 'Coverage on every signal, the tagged share of revenue, and what is missing.',
    who: ['founder', 'ops', 'performance'], cadence: 'On alarm, and week one',
    next: 'dashboard',
  },
  {
    key: 'integrations', to: '/integrations?cat=destinations', title: 'Integrations', stage: 'trust',
    asks: 'Is everything connected and sending?',
    gives: 'Connection state per platform and what each one is receiving.',
    who: ['ops'], cadence: 'On alarm',
    next: 'signal-health',
  },
  {
    key: 'dashboard', to: '/dashboard', title: 'Command Center', stage: 'position',
    asks: 'How am I doing, against the period before?',
    gives: 'Revenue, orders, MER, new customers, and what moved last week with both values.',
    who: ['founder', 'performance', 'retention'], cadence: 'Daily, and every Monday',
    next: 'campaigns',
  },
  {
    key: 'campaigns', to: '/growth/campaigns', title: 'Campaigns', stage: 'money',
    asks: 'What did each campaign return?',
    gives: 'Platform to campaign to ad set to ad, same columns at every level, plus what the platform claimed for the same spend.',
    who: ['founder', 'performance'], cadence: 'Daily',
    next: 'actions',
  },
  {
    key: 'attribution', to: '/commerce/attribution', title: 'Attribution', stage: 'money',
    asks: 'Which channels brought the revenue?',
    gives: 'Revenue by channel on measured touches rather than the platform’s own claim.',
    who: ['performance', 'founder'], cadence: 'Weekly',
    next: 'channel-roles',
  },
  {
    key: 'studio', to: '/studio', title: 'Signal Studio', stage: 'decide',
    asks: 'What is this store telling the ad platforms?',
    gives: 'The whole catalogue - events, parameters, audiences - with what is switched on and what has actually been sent.',
    who: ['performance', 'founder'], cadence: 'Monthly',
    next: 'actions',
  },
  {
    key: 'leaks', to: '/growth/leaks', title: 'Where you are losing money', stage: 'decide',
    asks: 'Which of the four ad money leaks do I have?',
    gives: 'One thing to fix, with the orders behind it - and an explicit list of what could not be judged.',
    who: ['founder', 'performance'], cadence: 'Monthly',
    next: 'actions',
  },
  {
    key: 'channel-roles', to: '/growth/channel-roles', title: 'Channel Roles', stage: 'money',
    asks: 'Does each channel find people, or close them?',
    gives: 'Where each channel sits in the path to an order, so you stop judging them all on last click.',
    who: ['performance'], cadence: 'Monthly',
    next: 'actions',
  },
  {
    key: 'actions', to: '/growth/actions', title: "Today's Actions", stage: 'decide',
    asks: 'What do I change today, and what is it worth?',
    gives: 'Scale, trim, hold or stop per campaign, priced on return at the top of its own range, with a budget plan that respects what a campaign can absorb in a week.',
    who: ['performance', 'founder'], cadence: 'Daily',
    next: 'funnels',
  },
  {
    key: 'creatives', to: '/growth/creatives', title: 'Creatives', stage: 'diagnose',
    asks: 'Which creatives work, and which are fatiguing?',
    gives: 'Spend and return per creative over time.',
    who: ['performance'], cadence: 'Weekly',
    next: 'audience',
  },
  {
    key: 'funnels', to: '/growth/funnels', title: 'Funnels', stage: 'diagnose',
    asks: 'Where do people leave, and what does it cost?',
    gives: 'The store funnel, each leak priced in money, and the same split by product.',
    who: ['founder', 'performance'], cadence: 'When a number looks wrong',
    next: 'buyers',
  },
  {
    key: 'buyers', to: '/growth/buyers', title: 'Who Bought', stage: 'diagnose',
    asks: 'Who is buying, how fast, and how do they pay?',
    gives: 'New against returning, payment, discount depth, decision speed - each against the period before.',
    who: ['retention', 'founder'], cadence: 'Monthly',
    next: 'customer-value',
  },
  {
    key: 'insights', to: '/commerce/insights', title: 'Analytics & Insights', stage: 'diagnose',
    asks: 'Products, channel credit, timing, identity.',
    gives: 'Seven reports for when the headline number needs taking apart.',
    who: ['performance', 'retention'], cadence: 'When digging',
    next: 'funnels',
  },
  {
    key: 'customer-value', to: '/growth/customer-value', title: 'Customer Value', stage: 'economics',
    asks: 'What does a customer cost, and when do they pay back?',
    gives: 'CAC, LTV and payback by acquisition channel, value by first product, and who comes back and how fast - and it says so when the cohorts are too young to judge.',
    who: ['founder', 'retention'], cadence: 'Monthly',
    // Profit & Economics retired 24 Sep 2026: campaign contribution is on
    // Campaigns, product profitability on Insights > Product.
    next: 'findings',
  },
  {
    key: 'segments', to: '/commerce/segments', title: 'Customer segments', stage: 'economics',
    asks: 'Who are my best customers, and who is slipping away?',
    gives: 'Groups of known customers you can act on.',
    who: ['retention'], cadence: 'Monthly',
    next: 'recovery',
  },
  {
    key: 'findings', to: '/growth/findings', title: 'What We Found', stage: 'growth',
    asks: 'What would I not have looked for?',
    gives: 'Standing findings, each naming the wrong conclusion as well as the right one.',
    who: ['founder', 'performance', 'retention'], cadence: 'Monthly',
    next: 'audience',
  },
  {
    key: 'audience', to: '/studio?tab=audiences', title: 'Audiences', stage: 'growth',
    asks: 'Who should I advertise to next?',
    gives: 'Audiences ranked on cost per add to cart, sized on who is actually matchable.',
    who: ['performance'], cadence: 'Monthly',
    next: 'recovery',
  },
  {
    key: 'recovery', to: '/commerce/recovery', title: 'Recovery', stage: 'growth',
    asks: 'How much sits in abandoned carts?',
    gives: 'Recoverable value and the contactable share behind it.',
    who: ['retention'], cadence: 'Weekly',
    next: 'recommendations',
  },
  {
    key: 'recommendations', to: '/commerce/recommendations', title: 'Recommendations', stage: 'growth',
    asks: 'What else can I show a buyer already here?',
    gives: 'On-site recommendations with a holdout, so the lift is measured rather than assumed.',
    who: ['retention'], cadence: 'Monthly',
    next: null,
  },
];

export const byKey = (k) => REPORTS.find((r) => r.key === k) || null;
export const byPath = (p) => REPORTS.find((r) => r.to === p) || null;

/** The reports one role is meant to read, in stage order. */
export function forRole(role) {
  const order = STAGES.map((s) => s.key);
  return REPORTS
    .filter((r) => role === 'all' || (r.who || []).includes(role))
    .slice()
    .sort((a, b) => order.indexOf(a.stage) - order.indexOf(b.stage));
}
